import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import PlayCircleFilled from "@mui/icons-material/PlayCircleFilled";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTheme } from "@mui/material/styles";
import ModalContainer from "components/ModalContainer";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import {
  DEFAULT_NVR_IMAGE_SETTINGS,
  NVR_ADVANCED_CHANNELS,
  NVR_ADVANCED_NAV,
  NVR_ADVANCED_SCENES,
} from "./const";

const ImageSliderRow = ({ label, value, onChange }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "110px 1fr 56px",
        alignItems: "center",
        gap: 2,
        py: 0.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>
        {label}
      </Typography>
      <Slider
        size="small"
        min={0}
        max={100}
        value={value}
        onChange={(_, next) => onChange(next)}
        sx={{ color: theme.palette.primary.main }}
      />
      <TextField
        size="small"
        value={value}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isNaN(next)) return;
          onChange(Math.min(100, Math.max(0, next)));
        }}
        inputProps={{ style: { textAlign: "center", padding: "4px 6px", fontSize: 13 } }}
        sx={{ width: 56 }}
      />
    </Box>
  );
};

const NvrAdvancedSettingsModal = ({ open, deviceName, onClose }) => {
  const theme = useTheme();
  const [navId, setNavId] = useState("display");
  const [displayTab, setDisplayTab] = useState("image");
  const [channel, setChannel] = useState("CH1");
  const [imageOpen, setImageOpen] = useState(true);
  const [imageSettings, setImageSettings] = useState(DEFAULT_NVR_IMAGE_SETTINGS);

  useEffect(() => {
    if (!open) return;
    setNavId("display");
    setDisplayTab("image");
    setChannel("CH1");
    setImageOpen(true);
    setImageSettings(DEFAULT_NVR_IMAGE_SETTINGS);
  }, [open]);

  const dirty = useMemo(
    () => JSON.stringify(imageSettings) !== JSON.stringify(DEFAULT_NVR_IMAGE_SETTINGS),
    [imageSettings],
  );

  const setImageValue = (key, value) => {
    setImageSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setImageSettings(DEFAULT_NVR_IMAGE_SETTINGS);
    onClose();
  };

  return (
    <ModalContainer display={open} modalWidth="1250px">
      <Stack spacing={1.5} sx={{ minHeight: 640 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography sx={{ fontSize: 18, fontWeight: "bold", color: "#172664" }}>
            {`${deviceName || "<DeviceName>"} Advanced Settings`}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close advanced settings" sx={{ p: 0.5 }}>
            <CloseOutlined style={{ fontSize: 18, color: "rgba(0,0,0,0.45)" }} />
          </IconButton>
        </Stack>

        <Box sx={{ display: "flex", flex: 1, minHeight: 560, borderTop: `1px solid ${theme.palette.primary.greyThread}` }}>
          <Box
            sx={{
              width: 168,
              flexShrink: 0,
              borderRight: `1px solid ${theme.palette.primary.greyThread}`,
              py: 1.5,
            }}
          >
            {NVR_ADVANCED_NAV.map((item) => {
              const selected = item.id === navId;
              return (
                <Box
                  key={item.id}
                  onClick={() => setNavId(item.id)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: selected ? 700 : 500,
                    color: selected ? theme.palette.primary.main : theme.palette.primary.black,
                    borderRight: selected ? `3px solid ${theme.palette.primary.main}` : "3px solid transparent",
                    "&:hover": { backgroundColor: "rgba(23,38,100,0.04)" },
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {navId === "display" ? (
              <>
                <Box sx={{ px: 2, pt: 1 }}>
                  <Tabs
                    value={displayTab}
                    onChange={(_, value) => setDisplayTab(value)}
                    sx={{
                      minHeight: 36,
                      "& .MuiTab-root": {
                        textTransform: "none",
                        minHeight: 36,
                        fontWeight: 700,
                        color: theme.palette.primary.greyFifth,
                      },
                      "& .Mui-selected": { color: `${theme.palette.primary.main} !important` },
                    }}
                  >
                    <Tab label="Image" value="image" />
                    <Tab label="OSD" value="osd" />
                  </Tabs>
                </Box>

                {displayTab === "image" ? (
                  <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          width: 360,
                          height: 180,
                          borderRadius: 1,
                          backgroundColor: "#8a8a8a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <PlayCircleFilled sx={{ fontSize: 56, color: "rgba(255,255,255,0.9)" }} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pt: 0.5 }}>
                        <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Channel</Typography>
                        <Select
                          size="small"
                          value={channel}
                          onChange={(event) => setChannel(event.target.value)}
                          MenuProps={{ sx: { zIndex: 1400 } }}
                          sx={{ minWidth: 88, height: 32, fontSize: 13 }}
                        >
                          {NVR_ADVANCED_CHANNELS.map((option) => (
                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Box>

                    <Box sx={{ borderTop: `1px solid ${theme.palette.primary.greyThread}`, pt: 1 }}>
                      <Box
                        onClick={() => setImageOpen((prev) => !prev)}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          py: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.primary.black }}>
                          Image
                        </Typography>
                        {imageOpen
                          ? <KeyboardArrowUpIcon sx={{ color: theme.palette.primary.greyFifth }} />
                          : <KeyboardArrowDownIcon sx={{ color: theme.palette.primary.greyFifth }} />}
                      </Box>
                      {imageOpen && (
                        <Box sx={{ maxWidth: 520, pl: 1 }}>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "110px 1fr",
                              alignItems: "center",
                              gap: 2,
                              py: 0.75,
                            }}
                          >
                            <Typography sx={{ fontSize: 13, color: theme.palette.primary.black }}>Scene</Typography>
                            <Select
                              size="small"
                              value={imageSettings.scene}
                              onChange={(event) => setImageValue("scene", event.target.value)}
                              MenuProps={{ sx: { zIndex: 1400 } }}
                              sx={{ width: 180, height: 32, fontSize: 13 }}
                            >
                              {NVR_ADVANCED_SCENES.map((option) => (
                                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </Box>
                          <ImageSliderRow
                            label="Brightness"
                            value={imageSettings.brightness}
                            onChange={(value) => setImageValue("brightness", value)}
                          />
                          <ImageSliderRow
                            label="Saturation"
                            value={imageSettings.saturation}
                            onChange={(value) => setImageValue("saturation", value)}
                          />
                          <ImageSliderRow
                            label="Contrast"
                            value={imageSettings.contrast}
                            onChange={(value) => setImageValue("contrast", value)}
                          />
                          <ImageSliderRow
                            label="Sharpness"
                            value={imageSettings.sharpness}
                            onChange={(value) => setImageValue("sharpness", value)}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: 13, color: theme.palette.primary.greyFifth }}>
                      OSD settings will be available later.
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ fontSize: 13, color: theme.palette.primary.greyFifth }}>
                  {`${NVR_ADVANCED_NAV.find((item) => item.id === navId)?.label || ""} settings will be available later.`}
                </Typography>
              </Box>
            )}
            <Stack
              direction="row"
              justifyContent="center"
              spacing={1.5}
              sx={{ mt: "auto", pt: 6, pb: 1 }}
            >
              <OutlineButton width="90px" height="36px" onClick={handleCancel}>
                Cancel
              </OutlineButton>
              <SolidButton width="90px" height="36px" disabled={!dirty} onClick={onClose}>
                Apply
              </SolidButton>
            </Stack>
          </Box>
        </Box>
      </Stack>
    </ModalContainer>
  );
};

export default NvrAdvancedSettingsModal;
